import { Fragment, useRef, useState } from 'react';
import { Button, Menu } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useUi } from 'hooks/useUi';
import { FilterChips } from './FilterChips/FilterChips';
import { FilterGroup, FilterGroupProps } from './FilterGroup/FilterGroup';
import { verticalHeightBreakpoint } from 'lib/constants';
import styles from './FilterBar.module.scss';

export interface FilterBarProps {
    filterGroups: FilterGroupProps[];
    /** By default, filter chips will appear below the filter dropdowns.
     * If you'd prefer to add the FilterChips component separately,
     * so it can be placed in a custom location, enable this field to hide the filter chips
     * below the dropdowns. */
    hideFilterChips?: boolean;
}

export const FilterBar = ({
    filterGroups,
    hideFilterChips,
}: FilterBarProps) => {
    const { ui } = useUi();
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const toolbarMenuButtonRef = useRef<HTMLButtonElement>(null);

    return (
        <div data-testid="FilterBar" className={styles.FilterBar}>
            {!ui.isMobile && ui.height >= verticalHeightBreakpoint && (
                <Fragment>
                    {/* Dropdown menu with checkboxes for each group of filters */}
                    <div className={styles.filterGroupContainer}>
                        {filterGroups.map(
                            (filterGroup: FilterGroupProps, i: number) => {
                                return (
                                    <FilterGroup
                                        key={i}
                                        name={filterGroup.name}
                                        filters={filterGroup.filters}
                                        onUpdateFilters={
                                            filterGroup.onUpdateFilters
                                        }
                                    />
                                );
                            }
                        )}
                    </div>

                    {hideFilterChips !== true && (
                        <FilterChips filterGroups={filterGroups} />
                    )}
                </Fragment>
            )}
            {!ui.isMobile && ui.height < verticalHeightBreakpoint && (
                <div className={styles.verticallyCompressedFilterBar}>
                    {/* Dropdown menu with checkboxes for each group of filters */}
                    <Button
                        variant="contained"
                        onClick={() => setMenuOpen(!menuOpen)}
                        endIcon={<KeyboardArrowDown />}
                        ref={toolbarMenuButtonRef}
                        sx={{ marginBottom: '.5rem' }}
                    >
                        Filters
                    </Button>
                    <Menu
                        onClose={() => setMenuOpen(!menuOpen)}
                        open={menuOpen}
                        anchorEl={toolbarMenuButtonRef.current}
                    >
                        {/* Dropdown menu with checkboxes for each group of filters */}
                        <div className={styles.filterGroupContainer}>
                            {filterGroups.map(
                                (filterGroup: FilterGroupProps, i: number) => {
                                    return (
                                        <FilterGroup
                                            key={i}
                                            name={filterGroup.name}
                                            filters={filterGroup.filters}
                                            onUpdateFilters={
                                                filterGroup.onUpdateFilters
                                            }
                                        />
                                    );
                                }
                            )}
                        </div>
                    </Menu>
                    {hideFilterChips !== true && (
                        <FilterChips filterGroups={filterGroups} />
                    )}
                </div>
            )}
            {ui.isMobile && (
                <Fragment>
                    <Button
                        variant="contained"
                        onClick={() => setMenuOpen(!menuOpen)}
                        endIcon={<KeyboardArrowDown />}
                        ref={toolbarMenuButtonRef}
                        sx={{ marginBottom: '.5rem' }}
                        className={styles.mobileFilterButton}
                    >
                        Filters
                    </Button>
                    <Menu
                        onClose={() => setMenuOpen(!menuOpen)}
                        open={menuOpen}
                        anchorEl={toolbarMenuButtonRef.current}
                    >
                        {/* Dropdown menu with checkboxes for each group of filters */}
                        <div className={styles.filterGroupContainer}>
                            {filterGroups.map(
                                (filterGroup: FilterGroupProps, i: number) => {
                                    return (
                                        <FilterGroup
                                            key={i}
                                            name={filterGroup.name}
                                            filters={filterGroup.filters}
                                            onUpdateFilters={
                                                filterGroup.onUpdateFilters
                                            }
                                        />
                                    );
                                }
                            )}
                        </div>
                        {hideFilterChips !== true && (
                            <FilterChips filterGroups={filterGroups} />
                        )}
                    </Menu>
                </Fragment>
            )}
        </div>
    );
};
